import React, { useState, useEffect } from 'react';
import { FiSearch, FiDownload, FiFileText, FiEye, FiX } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function CustomerDatabasePage() {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // 🚀 State สำหรับจัดการ Popup (Modal) รายละเอียดลูกค้า
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API_URL}?action=get_customers`);
      const data = await res.json();
      setCustomers(data);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredCustomers = customers.filter(c => 
    (c.name && c.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (c.phone && c.phone.includes(searchTerm))
  );

  // ==========================================
  // 🚀 ระบบ Export Excel
  // ==========================================
  const exportToExcel = () => {
    if (filteredCustomers.length === 0) return Swal.fire('แจ้งเตือน', 'ไม่มีข้อมูลให้ดาวน์โหลด', 'warning');
    
    // จัดเตรียมข้อมูลก่อนลงตาราง Excel
    const exportData = filteredCustomers.map((c, index) => ({
      'ลำดับ': index + 1,
      'ชื่อลูกค้า': c.name || '-',
      'เบอร์โทรศัพท์': c.phone || '-',
      'ที่อยู่': c.address || '-',
      'ยอดงานซ่อม (ครั้ง)': c.total_jobs || 0,
      'ยอดใช้จ่ายรวม (บาท)': c.total_spent || 0,
      'ใช้งานล่าสุด': c.last_active || '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
    XLSX.writeFile(workbook, `Customer_Database_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ==========================================
  // 🚀 ระบบ Export PDF (อัปเดตโค้ดใหม่)
  // ==========================================
  const exportToPDF = () => {
    if (filteredCustomers.length === 0) return Swal.fire('แจ้งเตือน', 'ไม่มีข้อมูลให้ดาวน์โหลด', 'warning');
    
    const doc = new jsPDF();
    
    const tableColumn = ["Name", "Phone", "Total Jobs", "Total Spent (THB)"];
    const tableRows = [];

    filteredCustomers.forEach(c => {
      const rowData = [
        c.name || '-',
        c.phone || '-',
        c.total_jobs || '0',
        c.total_spent || '0',
      ];
      tableRows.push(rowData);
    });

    // 🚀 จุดที่แก้ไข: เรียกใช้ autoTable แบบใหม่ และส่ง doc เข้าไปเป็นพารามิเตอร์ตัวแรก
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'grid'
    });
    
    doc.text("Customer Database Report", 14, 15);
    doc.save(`Customer_Report_${new Date().toISOString().split('T')[0]}.pdf`);
  };
  
  // 🚀 เปิด Popup ดูข้อมูลลูกค้า
  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  return (
    <div className="p-6 animate-fade-in relative">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-2xl font-black text-gray-800 dark:text-white flex items-center gap-3">
            👥 ฐานข้อมูลลูกค้า (CRM)
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">สรุปประวัติลูกค้า ยอดใช้จ่าย และการเรียกใช้บริการ</p>
        </div>
      </div>

      <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-2xl shadow-sm overflow-hidden">
        
        {/* แถบเครื่องมือ: ค้นหา & Export */}
        <div className="p-4 border-b border-gray-200 dark:border-[#1e2330] flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50/50 dark:bg-transparent">
          <div className="relative w-full md:w-96">
            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="ค้นหาชื่อ หรือ เบอร์โทรศัพท์..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white dark:bg-[#0b0f19] border border-gray-300 dark:border-[#334155] text-sm rounded-xl py-2.5 pl-10 pr-4 focus:outline-none focus:border-blue-500 text-gray-800 dark:text-white transition-colors"
            />
          </div>
          
          <div className="flex items-center gap-3 w-full md:w-auto">
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 mr-2">
              ลูกค้ารวม: <span className="text-blue-500">{filteredCustomers.length}</span> ท่าน
            </span>
            {/* 🚀 ปุ่มดาวน์โหลด Excel & PDF */}
            <button onClick={exportToExcel} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg text-xs font-bold transition shadow-sm">
              <FiFileText size={14} /> Excel
            </button>
            <button onClick={exportToPDF} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg text-xs font-bold transition shadow-sm">
              <FiDownload size={14} /> PDF
            </button>
          </div>
        </div>

        {/* ตารางข้อมูล */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100 dark:bg-[#1e2330] text-gray-600 dark:text-gray-400 text-xs font-bold uppercase tracking-wider">
                <th className="p-4 pl-6 border-b border-gray-200 dark:border-[#334155]">ชื่อลูกค้า</th>
                <th className="p-4 border-b border-gray-200 dark:border-[#334155]">เบอร์ติดต่อ</th>
                <th className="p-4 border-b border-gray-200 dark:border-[#334155]">ยอดงานซ่อม</th>
                <th className="p-4 border-b border-gray-200 dark:border-[#334155]">ยอดใช้จ่ายรวม</th>
                <th className="p-4 pr-6 border-b border-gray-200 dark:border-[#334155] text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length > 0 ? filteredCustomers.map((c, i) => (
                <tr key={i} className="hover:bg-blue-50 dark:hover:bg-[#1e293b] transition-colors group border-b border-gray-100 dark:border-[#1e2330]">
                  <td className="p-4 pl-6">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xs shrink-0">
                        {(c.name || 'C').charAt(0).toUpperCase()}
                      </div>
                      <span className="font-bold text-sm text-gray-800 dark:text-gray-200">{c.name || '-'}</span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-gray-600 dark:text-gray-300 font-medium">📞 {c.phone || '-'}</td>
                  <td className="p-4 text-sm font-bold text-gray-800 dark:text-gray-200">{c.total_jobs} งาน</td>
                  <td className="p-4 text-sm font-black text-green-600 dark:text-green-400">฿{Number(c.total_spent).toLocaleString()}</td>
                  <td className="p-4 pr-6 text-right">
                    {/* 🚀 ปุ่มกดดูรายละเอียด */}
                    <button 
                      onClick={() => handleViewCustomer(c)}
                      className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-200 px-3 py-1.5 rounded-lg text-xs font-bold transition"
                    >
                      <FiEye size={14} /> รายละเอียด
                    </button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="p-10 text-center text-gray-500 text-sm font-bold">ไม่พบข้อมูลลูกค้า</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ========================================== */}
      {/* 🚀 Modal (Popup) แสดงรายละเอียดลูกค้าเต็มๆ */}
      {/* ========================================== */}
      {isModalOpen && selectedCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white dark:bg-[#131825] w-full max-w-lg rounded-2xl shadow-2xl border border-gray-200 dark:border-[#334155] overflow-hidden">
            
            {/* Header ของ Modal */}
            <div className="bg-gray-100 dark:bg-[#1e2330] p-4 flex justify-between items-center border-b border-gray-200 dark:border-[#334155]">
              <h3 className="font-black text-gray-800 dark:text-white text-lg flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm">
                  {selectedCustomer.name?.charAt(0).toUpperCase() || 'C'}
                </span>
                ประวัติลูกค้า
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-red-500 transition p-1">
                <FiX size={24} />
              </button>
            </div>

            {/* เนื้อหาด้านใน Modal */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-[#0b0f19] p-3 rounded-xl border border-gray-100 dark:border-[#1e2330]">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">ชื่อ-นามสกุล</p>
                  <p className="font-bold text-gray-800 dark:text-white text-sm">{selectedCustomer.name || '-'}</p>
                </div>
                <div className="bg-gray-50 dark:bg-[#0b0f19] p-3 rounded-xl border border-gray-100 dark:border-[#1e2330]">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">เบอร์โทรศัพท์</p>
                  <p className="font-bold text-blue-600 dark:text-blue-400 text-sm">{selectedCustomer.phone || '-'}</p>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-[#0b0f19] p-3 rounded-xl border border-gray-100 dark:border-[#1e2330]">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">ที่อยู่ล่าสุดที่บันทึกไว้</p>
                <p className="font-medium text-gray-700 dark:text-gray-300 text-sm whitespace-pre-wrap">
                  {selectedCustomer.address || 'ไม่มีข้อมูลที่อยู่'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl text-center border border-blue-100 dark:border-blue-900/50">
                  <p className="text-2xl font-black text-blue-700 dark:text-blue-400">{selectedCustomer.total_jobs}</p>
                  <p className="text-xs font-bold text-blue-600 dark:text-blue-500 mt-1">จำนวนงานซ่อม</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-xl text-center border border-green-100 dark:border-green-900/50">
                  <p className="text-2xl font-black text-green-700 dark:text-green-400">฿{Number(selectedCustomer.total_spent).toLocaleString()}</p>
                  <p className="text-xs font-bold text-green-600 dark:text-green-500 mt-1">ยอดใช้จ่ายรวม</p>
                </div>
              </div>

              <div className="text-right mt-2">
                <p className="text-xs text-gray-400">อัปเดตล่าสุด: {selectedCustomer.last_active || '-'}</p>
              </div>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-[#1e2330] border-t border-gray-200 dark:border-[#334155]">
              <button 
                onClick={() => setIsModalOpen(false)}
                className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-[#334155] dark:hover:bg-gray-600 text-gray-800 dark:text-white font-bold py-3 rounded-xl transition shadow-sm text-sm"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}